<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

const props = defineProps({
  matchType: {
    type: String,
    required: true,
    validator: value => ['exact', 'keyword'].includes(value),
  },
  record: {
    type: Object,
    default: null,
  },
  prefillQuestion: {
    type: String,
    default: '',
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'cancel']);

const { t } = useI18n();

const draft = reactive({
  question: props.record?.question ?? props.prefillQuestion,
  keyword: props.record?.keyword ?? '',
  synonyms: props.record?.synonyms ?? [],
  keyword_match: props.record?.keyword_match ?? 'contains',
  answer: props.record?.answer ?? '',
  priority: props.record?.priority ?? 0,
  intent: props.record?.intent ?? '',
  // A new keyword answer is rephrased to fit the question by default; an existing
  // record keeps its own value.
  use_ai_rephrase:
    props.record?.use_ai_rephrase ?? props.matchType === 'keyword',
  include_in_context: props.record?.include_in_context ?? false,
  enabled: props.record?.enabled ?? true,
});

const keywordMatchOptions = computed(() => [
  {
    value: 'contains',
    label: t('AI_AGENT.ANSWERS.FIELDS.KEYWORD_MATCH_CONTAINS'),
  },
  {
    value: 'starts_with',
    label: t('AI_AGENT.ANSWERS.FIELDS.KEYWORD_MATCH_STARTS_WITH'),
  },
  {
    value: 'ends_with',
    label: t('AI_AGENT.ANSWERS.FIELDS.KEYWORD_MATCH_ENDS_WITH'),
  },
  {
    value: 'exact_phrase',
    label: t('AI_AGENT.ANSWERS.FIELDS.KEYWORD_MATCH_EXACT_PHRASE'),
  },
]);

const canSubmit = computed(() => {
  if (!draft.answer.trim()) return false;
  return props.matchType === 'exact'
    ? !!draft.question.trim()
    : !!draft.keyword.trim();
});

const submit = () => {
  if (!canSubmit.value) return;
  emit('save', {
    match_type: props.matchType,
    question: props.matchType === 'exact' ? draft.question.trim() : undefined,
    keyword: props.matchType === 'keyword' ? draft.keyword.trim() : undefined,
    synonyms: props.matchType === 'keyword' ? draft.synonyms : undefined,
    keyword_match:
      props.matchType === 'keyword' ? draft.keyword_match : undefined,
    answer: draft.answer,
    priority: Number(draft.priority) || 0,
    intent: draft.intent.trim() || null,
    use_ai_rephrase: draft.use_ai_rephrase,
    include_in_context: draft.include_in_context,
    enabled: draft.enabled,
  });
};
</script>

<template>
  <form
    class="flex flex-col gap-4"
    data-testid="answer-form"
    @submit.prevent="submit"
  >
    <Input
      v-if="matchType === 'exact'"
      v-model="draft.question"
      :label="$t('AI_AGENT.ANSWERS.FIELDS.QUESTION')"
      :placeholder="$t('AI_AGENT.ANSWERS.FIELDS.QUESTION_PLACEHOLDER')"
      data-testid="answer-question"
    />
    <template v-else>
      <Input
        v-model="draft.keyword"
        :label="$t('AI_AGENT.ANSWERS.FIELDS.KEYWORD')"
        :placeholder="$t('AI_AGENT.ANSWERS.FIELDS.KEYWORD_PLACEHOLDER')"
        data-testid="answer-keyword"
      />
      <div class="flex flex-col gap-1">
        <TagInput
          v-model="draft.synonyms"
          :placeholder="$t('AI_AGENT.ANSWERS.FIELDS.SYNONYMS')"
        />
        <p class="text-xs text-n-slate-10">
          {{ $t('AI_AGENT.ANSWERS.FIELDS.SYNONYMS_HELP') }}
        </p>
      </div>
      <Select
        v-model="draft.keyword_match"
        :options="keywordMatchOptions"
        :label="$t('AI_AGENT.ANSWERS.FIELDS.KEYWORD_MATCH')"
        data-testid="answer-keyword-match"
      />
    </template>
    <TextArea
      v-model="draft.answer"
      :label="$t('AI_AGENT.ANSWERS.FIELDS.ANSWER')"
      :placeholder="$t('AI_AGENT.ANSWERS.FIELDS.ANSWER_PLACEHOLDER')"
      data-testid="answer-text"
    />
    <div class="grid grid-cols-2 gap-4">
      <Input
        v-model="draft.priority"
        type="number"
        :label="$t('AI_AGENT.ANSWERS.FIELDS.PRIORITY')"
        data-testid="answer-priority"
      />
      <Input
        v-model="draft.intent"
        :label="$t('AI_AGENT.ANSWERS.FIELDS.INTENT')"
        :placeholder="$t('AI_AGENT.ANSWERS.FIELDS.INTENT_PLACEHOLDER')"
        data-testid="answer-intent"
      />
    </div>
    <div class="flex flex-col gap-2">
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <Switch v-model="draft.use_ai_rephrase" data-testid="answer-rephrase" />
        {{ $t('AI_AGENT.ANSWERS.FIELDS.USE_AI_REPHRASE') }}
      </label>
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <Switch
          v-model="draft.include_in_context"
          data-testid="answer-context"
        />
        {{ $t('AI_AGENT.ANSWERS.FIELDS.INCLUDE_IN_CONTEXT') }}
      </label>
      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <Switch v-model="draft.enabled" data-testid="answer-enabled" />
        {{ $t('AI_AGENT.ANSWERS.FIELDS.ENABLED') }}
      </label>
    </div>
    <div class="flex items-center justify-end gap-2">
      <Button
        variant="faded"
        slate
        :label="$t('AI_AGENT.ANSWERS.CANCEL')"
        data-testid="answer-cancel"
        @click="emit('cancel')"
      />
      <Button
        type="submit"
        :label="$t('AI_AGENT.ANSWERS.SAVE')"
        :is-loading="isSaving"
        :disabled="!canSubmit"
        data-testid="answer-submit"
      />
    </div>
  </form>
</template>
